import React, { useEffect, useState, useMemo } from 'react';
import { useAppStore } from '../context/AppStore';
import { Badge, NetworkIcon } from '../components/ui';
import { Link } from 'react-router-dom';

const STATUS_COLOR = { published:'#10b981', partial:'#f59e0b', failed:'#ef4444', scheduled:'#3b82f6' };
const DAYS_ES = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year, month) { return new Date(year, month, 1).getDay(); }

export default function CalendarPage() {
  const { posts, fetchPosts } = useAppStore();
  const [view, setView]       = useState('month');
  const [today]               = useState(new Date());
  const [current, setCurrent] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [selected, setSelected] = useState(null);

  useEffect(() => { fetchPosts(); }, []);

  const postsByDate = useMemo(() => {
    const map = {};
    posts.forEach(post => {
      const rawDate = post.scheduled_at || post.published_at || post.created_at;
      if (!rawDate) return;
      const dateStr = rawDate.endsWith('Z') ? rawDate : rawDate + 'Z';
      const d = new Date(dateStr);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (!map[key]) map[key] = [];
      map[key].push(post);
    });
    return map;
  }, [posts]);

  const prevMonth = () => setCurrent(c => c.month === 0 ? { year: c.year-1, month: 11 } : { year: c.year, month: c.month-1 });
  const nextMonth = () => setCurrent(c => c.month === 11 ? { year: c.year+1, month: 0 } : { year: c.year, month: c.month+1 });

  const daysInMonth = getDaysInMonth(current.year, current.month);
  const firstDay    = getFirstDayOfMonth(current.year, current.month);
  const todayStr    = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  const selectedKey   = selected ? `${current.year}-${String(current.month+1).padStart(2,'0')}-${String(selected).padStart(2,'0')}` : null;
  const selectedPosts = selectedKey ? (postsByDate[selectedKey] || []) : [];

  const monthPosts = Object.entries(postsByDate)
    .filter(([key]) => key.startsWith(`${current.year}-${String(current.month+1).padStart(2,'0')}`))
    .flatMap(([, ps]) => ps)
    .sort((a, b) => new Date(a.scheduled_at || a.created_at) - new Date(b.scheduled_at || b.created_at));

  const VIEWS = [
    { id: 'month', label: 'Mes' },
    { id: 'list', label: 'Lista' },
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <button onClick={prevMonth} style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', color:'var(--text-primary)', cursor:'pointer', padding:'7px 12px', borderRadius:'var(--radius-md)', fontSize:'16px' }}>‹</button>
          <h2 style={{ fontFamily:'var(--font-display)', fontWeight:'800', fontSize:'20px', minWidth:'200px', textAlign:'center' }}>
            {MONTHS_ES[current.month]} {current.year}
          </h2>
          <button onClick={nextMonth} style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', color:'var(--text-primary)', cursor:'pointer', padding:'7px 12px', borderRadius:'var(--radius-md)', fontSize:'16px' }}>›</button>
        </div>
        <div style={{ display:'flex', gap:'6px', alignItems:'center', flexWrap:'wrap' }}>
          <button onClick={() => setCurrent({ year: today.getFullYear(), month: today.getMonth() })} style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', color:'var(--text-muted)', cursor:'pointer', padding:'6px 12px', borderRadius:'var(--radius-md)', fontSize:'13px' }}>Hoy</button>
          {VIEWS.map(v => (
            <button key={v.id} onClick={() => setView(v.id)} style={{ padding:'6px 14px', borderRadius:'8px', fontSize:'13px', cursor:'pointer', background: view===v.id ? 'var(--gradient-primary)' : 'var(--bg-elevated)', border:`1px solid ${view===v.id ? 'transparent' : 'var(--border)'}`, color: view===v.id ? '#fff' : 'var(--text-muted)' }}>
              {v.label}
            </button>
          ))}
          <Link to="/publish" style={{ textDecoration:'none' }}>
            <button style={{ padding:'6px 14px', borderRadius:'8px', fontSize:'13px', cursor:'pointer', background:'var(--gradient-primary)', border:'none', color:'#fff', fontWeight:'600' }}>⚡ Publicar</button>
          </Link>
        </div>
      </div>

      {/* Vista mes */}
      {view === 'month' && (
        <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-xl)', overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', borderBottom:'1px solid var(--border)' }}>
            {DAYS_ES.map(d => (
              <div key={d} style={{ padding:'10px 0', textAlign:'center', fontSize:'12px', fontWeight:'700', color:'var(--text-muted)', borderRight:'1px solid var(--border)' }}>{d}</div>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)' }}>
            {Array.from({ length: firstDay }).map((_,i) => (
              <div key={`e-${i}`} style={{ minHeight:'90px', borderRight:'1px solid var(--border)', borderBottom:'1px solid var(--border)', background:'var(--bg-elevated)', opacity:0.4 }} />
            ))}
            {Array.from({ length: daysInMonth }).map((_,i) => {
              const day      = i + 1;
              const key      = `${current.year}-${String(current.month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const dayPosts = postsByDate[key] || [];
              const isToday  = key === todayStr;
              const isSel    = selected === day;
              return (
                <div key={day} onClick={() => setSelected(isSel ? null : day)} style={{ minHeight:'90px', padding:'6px', cursor:'pointer', borderRight:'1px solid var(--border)', borderBottom:'1px solid var(--border)', background: isSel ? '#7c3aed15' : isToday ? '#7c3aed08' : 'transparent', transition:'background 0.15s' }}>
                  <div style={{ width:'26px', height:'26px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight: isToday ? '800' : '500', marginBottom:'4px', background: isToday ? 'var(--gradient-primary)' : 'transparent', color: isToday ? '#fff' : isSel ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>
                    {day}
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'2px' }}>
                    {dayPosts.slice(0,4).map((p,pi) => (
                      <div key={`p${pi}`} style={{ width:'7px', height:'7px', borderRadius:'50%', background: STATUS_COLOR[p.status] || '#888', flexShrink:0 }} />
                    ))}
                    {dayPosts.length > 4 && <span style={{ fontSize:'9px', color:'var(--text-muted)' }}>+{dayPosts.length - 4}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Panel día seleccionado */}
      {view === 'month' && selected && selectedPosts.length > 0 && (
        <div className="animate-slideInUp" style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'16px' }}>
          <h3 style={{ fontFamily:'var(--font-display)', fontWeight:'700', fontSize:'15px', marginBottom:'12px' }}>
            {selected} de {MONTHS_ES[current.month]}
          </h3>
          <div style={{ fontSize:'12px', fontWeight:'700', color:'var(--text-muted)', marginBottom:'6px' }}>PUBLICACIONES</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            {selectedPosts.map(p => <PostRow key={p.id} post={p} />)}
          </div>
        </div>
      )}

      {/* Vista lista posts */}
      {view === 'list' && (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {monthPosts.length === 0
            ? <div style={{ textAlign:'center', padding:'40px', color:'var(--text-muted)', fontSize:'14px' }}>Sin publicaciones en {MONTHS_ES[current.month]}. <Link to="/publish" style={{ color:'var(--accent-primary)' }}>Crear una →</Link></div>
            : monthPosts.map(p => <PostRow key={p.id} post={p} showDate />)}
        </div>
      )}

      {/* Leyenda */}
      <div style={{ display:'flex', gap:'16px', flexWrap:'wrap', padding:'12px 16px', background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', border:'1px solid var(--border)' }}>
        <div style={{ fontSize:'12px', fontWeight:'700', color:'var(--text-muted)' }}>Posts:</div>
        {Object.entries({ published:'Publicado', scheduled:'Programado', partial:'Parcial', failed:'Fallido' }).map(([s,l]) => (
          <div key={s} style={{ display:'flex', alignItems:'center', gap:'6px' }}>
            <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:STATUS_COLOR[s] }} />
            <span style={{ fontSize:'12px', color:'var(--text-muted)' }}>{l}</span>
          </div>
        ))}
      </div>

    </div>
  );
}

function PostRow({ post, showDate = false }) {
  const date = post.scheduled_at || post.published_at || post.created_at;
  const caption = post.networks.find(n => n.caption)?.caption || '';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 14px', borderRadius:'var(--radius-md)', background:'var(--bg-elevated)', border:`1px solid ${STATUS_COLOR[post.status]}30` }}>
      <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:STATUS_COLOR[post.status], flexShrink:0 }} />
      <div style={{ display:'flex', gap:'4px', flexShrink:0 }}>
        {post.networks.map(n => <NetworkIcon key={n.network} network={n.network} size={16} />)}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        {showDate && <div style={{ fontSize:'11px', color:'var(--accent-primary)', fontWeight:'600', marginBottom:'2px' }}>{new Date(date).toLocaleString('es', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })}</div>}
        <p style={{ fontSize:'13px', color:'var(--text-secondary)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{caption || `Post ${post.media_type}`}</p>
      </div>
      <Badge color={STATUS_COLOR[post.status]} style={{ flexShrink:0, fontSize:'11px' }}>
        {post.status === 'published' ? '✓' : post.status === 'scheduled' ? '⏰' : post.status === 'failed' ? '✕' : '⚠'}
      </Badge>
    </div>
  );
}
